<template>
  <div class="table-page">
    <div class="loading loading-lg mt-2" v-if="loading || saving" />
    <div v-else>
      <div class="empty mt-2" v-if="data.items.length === 0">
        <div class="empty-icon">
          <fa-icon :icon="['fal', 'info-circle']" size="3x"/>
        </div>
        <p class="empty-title h5">
          <span v-if="isExpenseOrigin">Tabelas de preços</span>
          <span v-else>Serviços, taxas e outras despesas</span>
        </p>
        <p class="empty-subtitle">
          <span v-if="isExpenseOrigin">Nenhuma tabela de preço vinculada a este serviço</span>
          <span v-else>Nenhum serviço vinculado a esta tabela</span>
        </p>
        <div class="action">
          <button class="btn btn-primary btn-icon btn-icon-left mt-2"
                  @click="openFindItems">
            <fa-icon :icon="['fal', 'plus']" />
            <span v-if="isExpenseOrigin">Vincular tabelas</span>
            <span v-else>Vincular serviços</span>
          </button>
        </div>
      </div>
      <div v-else>
        <div class="columns stenci-group">
          <div class="column col-4 col-md-6 col-md-12 form-group" v-if="!isExpenseOrigin">
            <label class="form-label">Filtrar tipo</label>
            <select class="form-select" v-model="filter.type">
              <option value="">Todos</option>
              <option v-for="(text, value) in expenseTypes"
                      :value="value" :key="value">{{ text }}</option>
            </select>
          </div>
          <div class="column form-group">
            <label class="form-label">Pesquisar</label>
            <div class="input-group">
              <input
                type="text"
                class="form-input"
                v-model="filter.search"
                placeholder="Pesquisar por parte do nome...">
              <button class="btn btn-neutral btn-action input-group-btn btn-icon"
                      tabindex="-1">
                <fa-icon :icon="['fal', 'search']" />
              </button>
            </div>
          </div>
          <div class="column col-auto form-group" style="align-self: flex-end">
            <button class="btn btn-icon btn-icon-left btn-primary mb-2"
                    :disabled="disabledFields"
                    @click="openFindItems">
              <fa-icon :icon="['fal', 'plus']" />
              <span v-if="isExpenseOrigin">Vincular tabelas</span>
              <span v-else>Vincular serviços</span>
            </button>
          </div>
        </div>
        <div class="empty mt-2" v-if="filteredExpenses.length === 0">
          <div class="empty-icon">
            <fa-icon :icon="['fal', 'info-circle']" size="3x"/>
          </div>
          <p class="empty-title h5">
            <span v-if="isExpenseOrigin">Tabelas de preços</span>
            <span v-else>Serviços, taxas e outras despesas</span>
          </p>
          <p class="empty-subtitle">
            <span v-if="isExpenseOrigin">
              Nenhuma tabela de preço encontrada com este filtro
            </span>
            <span v-else>Nenhum serviço encontrado com este filtro</span>
          </p>
        </div>
        <div v-else>
          <table class="table table-hover">
            <thead>
            <tr>
              <th class="hide-md hide-sm" style="width: 40px">#</th>
              <th>
                <span v-if="isExpenseOrigin">Nome da tabela</span>
                <span v-else>Nome do serviço</span>
              </th>
              <th class="hide-md hide-sm" style="width: 115px">Código</th>
              <th class="hide-md hide-sm" style="width: 80px">Tabela</th>
              <th class="text-right" style="width: 120px">Valor plano</th>
              <th class="text-right" style="width: 120px">Valor paciente</th>
              <th class="text-right" style="width: 140px">Valor repasse</th>
              <th style="width: 40px" />
            </tr>
            </thead>
            <tbody>
            <tr v-for="(item, i) in filteredExpenses" :key="i">
              <td class="text-center hide-md hide-sm">
                <div class="loading" v-if="(updating || opening) && idx === i" />
                <div v-else>{{ i + 1 }}</div>
              </td>
              <td class="text-left">
              <span class="hover-link"
                    @click="openExpenseUpdate(item, i)"
                    v-if="isExpenseOrigin">
                {{ item.name }}
              </span>
                <span class="hover-link"
                      @click="openExpenseUpdate(item, i)"
                      v-else>
                {{ item.expense.name }}
              </span>
              </td>
              <td class="text-center hide-md hide-sm"
                  :class="{'has-error': $v.data.items.$each[
                  getIndex(item)].code.$error}">
                <input
                  type="text"
                  class="form-input input-sm"
                  v-model="item.code"
                  :disabled="disabledFields"
                  @change="updateItem(item, i)"
                  @blur="$v.data.items.$each[getIndex(item)].code.$touch()"
                  v-mask="'00.00.00.00.00'"
                  placeholder="00.00.00.00"
                >
              </td>
              <td class="hide-md hide-sm"
                  :class="{'has-error': $v.data.items.$each[
                  getIndex(item)].tableCode.$error}">
                <select
                  class="form-select select-sm"
                  v-model="item.tableCode"
                  :disabled="disabledFields"
                  @change="updateItem(item, i)"
                  @blur="$v.data.items.$each[getIndex(item)].tableCode.$touch()">
                  <option value="">[Selecione]</option>
                  <option
                    v-for="(text, value) in expenseTables"
                    :value="value"
                    :key="value"
                  >{{ value }}
                  </option>
                </select>
              </td>
              <td class="text-right">
                <dx-input-number
                  class="form-input input-sm"
                  v-model="item.definition.insuranceValue"
                  @change="updateItem(item, i)"
                  :disabled="disabledFields"
                  :precision="2"
                />
              </td>
              <td class="text-right">
                <dx-input-number
                  class="form-input input-sm"
                  v-model="item.definition.particularValue"
                  @change="updateItem(item, i)"
                  :disabled="disabledFields"
                  :precision="2"
                />
              </td>
              <td class="text-right">
                <div class="input-group">
                  <dx-input-number
                    class="form-input input-sm"
                    v-model="item.definition.transferValue"
                    @change="updateItem(item, i)"
                    :disabled="disabledFields"
                    :precision="2" maxlength="7"
                  />
                  <select
                    class="form-select select-sm"
                    v-model="item.definition.transferType"
                    @change="updateItem(item, i)"
                    :disabled="disabledFields"
                    style="max-width: 2.5rem; background-color: #f8f9fa">
                    <option value="percent">%</option>
                    <option value="value">R$</option>
                  </select>
                </div>
              </td>
              <td>
                <button
                  class="btn btn-sm btn-action btn-icon btn-error"
                  :class="{loading: deleting && idx === i}"
                  @click="removeItem(item)"
                  :disabled="disabledFields || updating"
                >
                  <fa-icon :icon="['fal', 'times']" />
                </button>
              </td>
            </tr>
            </tbody>
          </table>
          <div class="action-group" v-if="!isExpenseOrigin">
            <button class="btn btn-warning btn-icon btn-icon-left mr-2"
                    @click="openBulkUpdateModal">
              <fa-icon :icon="['fal', 'layer-group']" />Atualização em massa
            </button>
            <button class="btn btn-secondary btn-icon btn-icon-left"
                    v-if="isCbhpm"
                    @click="openCBHPMModal">
              <fa-icon :icon="['fal', 'sync']" />Atualização via CBHPM
            </button>
          </div>
        </div>
      </div>
    </div>

    <find-expense-modal
      v-if="expenseModalShow"
      @selected="saveItems"
      @close="expenseModalShow = false"
    />

    <find-table-modal
      v-if="tableModalShow"
      @selected="saveItems"
      @close="tableModalShow = false"
    />

    <update-expense-modal
      v-if="updateExpenseModal.show"
      :expense-data="updateExpenseModal.data"
      @save="updateItem"
      @close="updateExpenseModal.show = false"
    />

    <update-bulk-modal
      v-if="updateBulkModalShow"
      :table-id="tableId"
      :filtered-ids="filteredExpenses.map(({ expense }) => expense.id)"
      :expense-ids="data.items.map(({ expense }) => expense.id)"
      @saved="savedBulk"
      @close="updateBulkModalShow = false"
    />

    <update-by-CBHPM-modal
      v-if="updateByCBHPMModalShow"
      :table-id="tableId"
      @saved="savedCBHPM"
      @close="updateByCBHPMModalShow = false"
    />
  </div>
</template>

<script>
import { tables as expenseTables } from '@/data/expense-tables';
import { required } from 'vuelidate/lib/validators';
import fuzzy from 'fuzzy';
import { mergeFrom } from '@/helpers/object';
import { types as expenseTypes } from 'src/data/expense-types';
import formMixin from '@/mixins/form';
import UpdateBulkModal from './modals/UpdateBulkModal.vue';
import FindExpenseModal from '../../expense/components/modals/FindExpenseModal.vue';
import FindTableModal from './modals/FindTableModal.vue';
import UpdateExpenseModal from './modals/UpdateExpenseModal.vue';
import UpdateByCBHPMModal from './modals/UpdateByCBHPMModal.vue';

export default {
  mixins: [formMixin],
  props: {
    origin: {
      type: String,
      required: true,
      enum: ['expense', 'table'],
    },
    expenseId: {
      type: String,
    },
    tableId: {
      type: String,
    },
    isCbhpm: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    UpdateBulkModal,
    FindExpenseModal,
    FindTableModal,
    UpdateExpenseModal,
    UpdateByCBHPMModal,
  },
  data() {
    return {
      idx: null,
      selectedItem: null,
      loading: false,
      saving: false,
      updating: false,
      deleting: false,
      opening: false,
      expenseTables,
      expenseTypes,
      tableModalShow: false,
      expenseModalShow: false,
      filter: {
        type: '',
        search: '',
      },
      data: {
        items: [],
        hasMore: false,
      },
      updateExpenseModal: {
        show: false,
        data: null,
      },
      updateBulkModalShow: false,
      updateByCBHPMModalShow: false,
    };
  },
  mounted() {
    if (this.expenseId || this.tableId) {
      this.load();
    }
  },
  computed: {
    disabledFields() {
      return this.deleting || this.opening;
    },
    isExpenseOrigin() {
      return this.origin === 'expense';
    },
    filteredExpenses() {
      let { items } = this.data;

      if (this.filter.type) {
        items = items.filter(item => item.expense.type === this.filter.type);
      }

      const search = this.filter.search.trim();

      if (search) {
        const results = fuzzy.filter(search, items, {
          extract: (el) => {
            if (this.isExpenseOrigin) {
              if (el.name) {
                return this.removeAccentuation(el.name);
              }
            } else if (el.expense.name) {
              return this.removeAccentuation(el.expense.name);
            }
            return '';
          },
        });
        items = results.map(({ original }) => original);
      }

      return items;
    },
  },
  validations() {
    return {
      data: {
        items: {
          $each: {
            code: { required },
            tableCode: { required },
          },
        },
      },
    };
  },
  methods: {
    load() {
      this.loading = true;

      const params = {
        active: true,
        limit: 0,
      };

      if (this.expenseId) {
        params.expenseId = this.expenseId;
      }

      if (this.tableId) {
        params.tableId = this.tableId;
      }

      return this.$http.get('/expense-tables', { params })
        .then(({ data }) => {
          this.data.hasMore = data.hasMore;
          this.data.items = data.items;
        })
        .catch(() => {
          this.$toast.error('Erro ao carregar os dados!');
        })
        .finally(() => {
          this.loading = false;
        });
    },
    getIndex(item) {
      return this.data.items.indexOf(item);
    },
    saveItems(selectedItems) {
      this.expenseModalShow = false;
      this.tableModalShow = false;

      const savedIds = this.isExpenseOrigin
        ? this.data.items.map(item => item.id)
        : this.data.items.map(item => item.expense.id);

      selectedItems = selectedItems.filter((item) => {
        if (this.isExpenseOrigin) {
          return !savedIds.includes(item.id);
        }
        return !savedIds.includes(item.id);
      });

      if (selectedItems.length === 0) {
        return null;
      }

      this.saving = true;

      const items = selectedItems
        .map(item => ({
          tableId: this.isExpenseOrigin ? item.id : this.tableId,
          expenseId: this.isExpenseOrigin ? this.expenseId : item.id,
        }));

      return this.$http
        .post('/expense-tables/expenses', { items })
        .then(({ data }) => {
          this.data.items = [...this.data.items, ...data.items];
          this.$toast.success('Registros adicionados com sucesso!');
        })
        .catch(() => {
          this.$toast.error('Erro ao salvar os dados!');
        }).finally(() => {
          this.saving = false;
        });
    },
    removeItem(item) {
      this.idx = this.data.items.indexOf(item);
      this.$dialog.show('', {
        html:
          '<div class="text-center">'
          + '<h5 class="text-center">Atenção!</h5>'
          + '<div>Deseja realmente excluir este registro?</div>'
          + '</div>',
        buttons: [
          {
            label: 'Não',
            classes: '',
          }, {
            label: 'Sim',
            classes: 'btn-primary btn-error ml-2',
            click: (close) => {
              close();
              this.deleting = true;
              this.$http.delete(`/expense-tables/${item.id}/expenses/${item.expense.id}`)
                .then(() => {
                  this.data.items.splice(this.idx, 1);
                })
                .catch(() => {
                  this.$toast.show('Ocorreu um erro. Tente novamente!', { type: 'error' });
                })
                .finally(() => {
                  this.deleting = false;
                });
            },
          },
        ],
      });
    },
    openFindItems() {
      if (this.isExpenseOrigin) {
        this.tableModalShow = true;
      } else {
        this.expenseModalShow = true;
      }
    },
    updateItem(item, idx) {
      if (idx !== undefined) {
        const validation = this.$v.data.items.$each[this.getIndex(item)];
        validation.$touch();
        if (validation.$error) {
          this.$toast.error('Informe o código e a tabela!');
          return null;
        }
      }

      this.updating = true;
      this.updateExpenseModal.show = false;

      if (idx === undefined) {
        const localItem = this.selectedItem;
        localItem.code = item.code;
        localItem.tableCode = item.tableCode;
        localItem.definition.insuranceValue = item.definition.insuranceValue;
        localItem.definition.particularValue = item.definition.particularValue;
        localItem.definition.transferValue = item.definition.transferValue;
        localItem.definition.transferType = item.definition.transferType;
        localItem.definition.requestingTransferValue = item.definition.requestingTransferValue;
        localItem.definition.requestingTransferType = item.definition.requestingTransferType;
      } else {
        this.idx = idx;
      }

      const data = {};

      [
        'code',
        'tableCode',
        'requireAuthorization',
        'requireClinicalIndication',
        'requireWayTechnique',
        'return',
      ].forEach((key) => {
        if (key in item) { data[key] = item[key]; }
      });

      [
        'accreditation',
        'accredited',
        'chQuantity',
        'chValue',
        'discount',
        'feeValue',
        'film',
        'filmQuantity',
        'filmValue',
        'insuranceTax',
        'interest',
        'operationalCost',
        'operationalCostValue',
        'postageCode',
        'priorAuthorization',
        'rate',
        'reportValue',
        'secondSegment',
        'tax',
        'taxType',
        'taxValue',
        'updateValuesByCbhpm',
        'insuranceValue',
        'particularValue',
        'transferValue',
        'transferType',
        'requestingTransferValue',
        'requestingTransferType',
      ].forEach((key) => {
        if (item.definition && key in item.definition) {
          data.definition = data.definition || {};
          data.definition[key] = item.definition[key];
        }
      });

      return this.$http
        .put(`/expense-tables/${item.id}/expenses/${item.expense.id}`, data)
        .catch(() => {
          this.$toast.error('Erro ao salvar os dados!');
        }).finally(() => {
          this.updating = false;
        });
    },
    openExpenseUpdate(item, i) {
      if (this.disabledFields) {
        return null;
      }

      this.idx = i;
      this.selectedItem = item;
      this.opening = true;

      return this.$http.get(`/expense-tables/${item.id}/expenses/${item.expense.id}`)
        .then(({ data }) => {
          this.updateExpenseModal.data = mergeFrom(this.blankExpense(), data);
          this.updateExpenseModal.show = true;
        })
        .catch(() => {
          this.$toast.error('Erro ao carregar os dados!');
        })
        .finally(() => {
          this.opening = false;
        });
    },
    openBulkUpdateModal() {
      this.updateBulkModalShow = true;
    },
    savedBulk() {
      this.updateBulkModalShow = false;
      this.load();
    },
    openCBHPMModal() {
      this.updateByCBHPMModalShow = true;
    },
    savedCBHPM() {
      this.updateByCBHPMModalShow = false;
      this.load();
    },
    blankExpense() {
      return {
        id: '',
        name: '',
        code: '',
        tableCode: '',
        return: null,
        breakInsurance: false,
        expense: {
          id: '',
          type: '',
          name: '',
          modality: '',
          specialty: {
            code: '',
            name: '',
          },
          billAs: null,
        },
        requireAuthorization: false,
        requireClinicalIndication: false,
        requireWayTechnique: false,
        professionals: [],
        definition: this.blankDefinition(),
      };
    },
    blankDefinition() {
      return {
        accreditation: {
          enabled: false,
          professional: null,
        },
        accredited: 'pj',
        chValue: '',
        chQuantity: '',
        secondSegment: '',
        operationalCostValue: '',
        filmValue: '',
        filmQuantity: '',
        feeValue: '',
        insuranceValue: 0,
        particularValue: 0,
        taxValue: '',
        taxType: 'pj',
        transferValue: 0,
        transferType: 'value',
        requestingTransferValue: 0,
        requestingTransferType: 'value',
        postageCode: '',
        tax: true,
        rate: true,
        discount: true,
        interest: true,
        operationalCost: true,
        film: true,
        insuranceTax: true,
        updateValuesByCbhpm: true,
        priorAuthorization: false,
        reportValue: '',
      };
    },
  },
};
</script>

<style lang="scss">
@import "assets/scss/_variables";

.table-page {
  .action-group {
    border: $border-width solid $border-color;
    border-radius: $border-radius;
    margin: $layout-spacing-lg 0;
    padding: $layout-spacing-lg;
    text-align: center;
    .btn {
      padding: $layout-spacing-lg;
    }
  }
}
</style>
